// components/features/tournaments/components/LineupValidationPanel.jsx
import React from 'react';
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  UserCheck, 
  UserX,
  Shield,
  AlertCircle
} from '../../../common/Icons';
import UserAvatar from '../../../common/UserAvatar';
import { FIELD_COMPARISON } from '../../../../utils/comparisonEngine';

const LineupValidationPanel = ({ lineupComparison, team1Name, team2Name }) => {
  if (!lineupComparison) {
    return (
      <div className="p-4 text-center text-gray-400">
        <Users className="w-6 h-6 mx-auto mb-2" />
        <p className="text-sm">No lineup data to validate</p>
      </div>
    );
  }

  const getValidationIcon = (isValid) => {
    return isValid ? (
      <CheckCircle className="w-5 h-5 text-green-400" />
    ) : (
      <XCircle className="w-5 h-5 text-red-500" />
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case FIELD_COMPARISON.MATCH:
        return 'border-green-500/40 bg-green-900/20';
      case FIELD_COMPARISON.DIFFER:
        return 'border-red-500/40 bg-red-900/20';
      default:
        return 'border-yellow-500/40 bg-yellow-900/20';
    }
  };

  const TeamValidationCard = ({ validation, teamName, isLeft = true }) => {
    if (!validation) {
      return (
        <div className="p-3 border border-white/10 rounded-lg bg-[#22262f]">
          <div className="text-center text-gray-400">
            <AlertCircle className="w-5 h-5 mx-auto mb-2" />
            <p className="text-sm">No roster data available</p>
          </div>
        </div>
      );
    }

    return (
      <div className={`p-3 rounded-lg border ${
        validation.valid
          ? 'border-green-500/40 bg-green-900/20'
          : 'border-red-500/40 bg-red-900/20'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-gray-400" />
            <h4 className="font-semibold text-white">
              {teamName} Lineup
            </h4>
          </div>
          {getValidationIcon(validation.valid)}
        </div>

        {/* Validation Status */}
        <div className="mb-2">
          <p className={`text-xs ${
            validation.valid
              ? 'text-green-400'
              : 'text-red-300'
          }`}>
            {validation.reason}
          </p>
        </div>

        {/* Player Counts */}
        <div className="grid grid-cols-2 gap-2 mb-2 text-sm">
          <div className="text-center p-2 bg-[#1e2129] rounded border border-white/[0.06]">
            <div className="font-bold text-white">
              {validation.validPlayers?.length || 0}
            </div>
            <div className="text-xs text-green-400">Valid</div>
          </div>
          <div className="text-center p-2 bg-[#1e2129] rounded border border-white/[0.06]">
            <div className="font-bold text-white">
              {validation.invalidPlayers?.length || 0}
            </div>
            <div className="text-xs text-red-400">Invalid</div>
          </div>
        </div>

        {/* Invalid Players List */}
        {validation.invalidPlayers && validation.invalidPlayers.length > 0 && (
          <div className="space-y-2">
            <h5 className="text-xs font-semibold text-red-300 flex items-center space-x-1">
              <UserX className="w-3 h-3" />
              <span>Invalid Players:</span>
            </h5>
            <div className="space-y-1">
              {validation.invalidPlayers.map((playerId, index) => (
                <div key={playerId} className="flex items-center space-x-2 p-2 bg-red-900/30 rounded text-sm">
                  <UserAvatar
                    user={{
                      id: playerId,
                      username: `Player ${playerId.slice(-4)}`
                    }}
                    size="w-6 h-6"
                    textSize="text-xs"
                    clickable={false}
                  />
                  <span className="text-red-300">
                    Player {playerId.slice(-8)}
                  </span>
                  <XCircle className="w-3 h-3 text-red-500 ml-auto" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Valid Players Preview */}
        {validation.validPlayers && validation.validPlayers.length > 0 && (
          <div className="mt-2">
            <h5 className="text-xs font-semibold text-green-400 flex items-center space-x-1 mb-2">
              <UserCheck className="w-3 h-3" />
              <span>Valid Players ({validation.validPlayers.length}):</span>
            </h5>
            <div className="flex flex-wrap gap-1">
              {validation.validPlayers.slice(0, 5).map((playerId) => (
                <UserAvatar
                  key={playerId}
                  user={{
                    id: playerId,
                    username: `Player ${playerId.slice(-4)}`
                  }}
                  size="w-6 h-6"
                  textSize="text-xs"
                  clickable={false}
                />
              ))}
              {validation.validPlayers.length > 5 && (
                <div className="w-6 h-6 bg-white/10 rounded-full flex items-center justify-center">
                  <span className="text-xs text-gray-400">
                    +{validation.validPlayers.length - 5}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const LineupComparisonSummary = () => {
    const { summary, differences, isPartialSubmission, submittingTeam } = lineupComparison;
    
    return (
      <div className={`p-3 rounded-lg border ${getStatusColor(lineupComparison.status)}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-white flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>{isPartialSubmission ? 'Lineup Review' : 'Lineup Comparison'}</span>
          </h4>
          {isPartialSubmission ? (
            <CheckCircle className="w-5 h-5 text-[#23F8B5]" />
          ) : lineupComparison.playersMatch ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3 mb-2">
          <div className="text-center p-2 bg-[#1e2129] rounded border border-white/[0.06]">
            <div className="font-bold text-white">
              {summary?.team1PlayerCount || 0}
            </div>
            <div className="text-xs text-gray-400">{team1Name}</div>
          </div>

          <div className="text-center p-2 bg-[#1e2129] rounded border border-white/[0.06]">
            <div className="font-bold text-white">
              {isPartialSubmission ? (summary?.team1PlayerCount || summary?.team2PlayerCount || 0) : (summary?.commonPlayers || 0)}
            </div>
            <div className="text-xs text-[#23F8B5]">
              {isPartialSubmission ? 'Submitted' : 'Common'}
            </div>
          </div>

          <div className="text-center p-2 bg-[#1e2129] rounded border border-white/[0.06]">
            <div className="font-bold text-white">
              {summary?.team2PlayerCount || 0}
            </div>
            <div className="text-xs text-gray-400">{team2Name}</div>
          </div>
        </div>

        {/* Status Message */}
        <div className={`text-xs ${
          isPartialSubmission
            ? 'text-[#23F8B5]'
            : lineupComparison.playersMatch
              ? 'text-green-400'
              : 'text-red-300'
        }`}>
          {isPartialSubmission
            ? `📋 ${submittingTeam === 'team1' ? team1Name : team2Name} submitted lineup for review`
            : lineupComparison.playersMatch
              ? '✅ Both teams reported the same players'
              : '❌ Teams reported different players'
          }
        </div>

        {/* Player List for Partial Submissions */}
        {isPartialSubmission && differences?.submittedPlayers && (
          <div className="mt-2">
            <p className="text-xs font-medium text-[#23F8B5] mb-2">
              Players in {submittingTeam === 'team1' ? team1Name : team2Name} submission:
            </p>
            <div className="flex flex-wrap gap-1">
              {differences.submittedPlayers.map((player) => (
                <span key={player.id} className="text-xs bg-[#31C590]/10 text-[#23F8B5] px-2 py-1 rounded flex items-center space-x-1">
                  {player.avatar && (
                    <img 
                      src={player.avatar} 
                      alt={player.name}
                      className="w-3 h-3 rounded-full"
                    />
                  )}
                  <span>{player.name}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Differences for Full Comparisons */}
        {!isPartialSubmission && !lineupComparison.playersMatch && differences && (
          <div className="mt-2 space-y-2">
            {differences.onlyInTeam1Submission && differences.onlyInTeam1Submission.length > 0 && (
              <div className="p-2 bg-[#31C590]/10 rounded">
                <p className="text-xs font-medium text-[#23F8B5] mb-1">
                  Only in {team1Name} submission:
                </p>
                <div className="flex flex-wrap gap-1">
                  {differences.onlyInTeam1Submission.map((playerId) => (
                    <span key={playerId} className="text-xs bg-[#31C590]/20 text-[#23F8B5] px-2 py-1 rounded">
                      {playerId.slice(-8)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {differences.onlyInTeam2Submission && differences.onlyInTeam2Submission.length > 0 && (
              <div className="p-2 bg-purple-900/30 rounded">
                <p className="text-xs font-medium text-purple-300 mb-1">
                  Only in {team2Name} submission:
                </p>
                <div className="flex flex-wrap gap-1">
                  {differences.onlyInTeam2Submission.map((playerId) => (
                    <span key={playerId} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                      {playerId.slice(-8)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2 mb-2">
        <Users className="w-5 h-5 text-gray-400" />
        <h3 className="text-base font-bold text-white">
          Lineup Validation & Comparison
        </h3>
      </div>

      {/* Lineup Comparison Summary */}
      <LineupComparisonSummary />

      {/* Team Validations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto">
        <TeamValidationCard 
          validation={lineupComparison.team1Validation} 
          teamName={team1Name}
          isLeft={true}
        />
        <TeamValidationCard 
          validation={lineupComparison.team2Validation} 
          teamName={team2Name}
          isLeft={false}
        />
      </div>

      {/* Overall Status */}
      <div className={`p-3 rounded-lg border ${
        lineupComparison.isPartialSubmission
          ? 'border-[#31C590]/40 bg-[#31C590]/10'
          : lineupComparison.status === FIELD_COMPARISON.MATCH
            ? 'border-green-500/40 bg-green-900/20'
            : 'border-red-500/40 bg-red-900/20'
      }`}>
        <div className="flex items-center space-x-2">
          {lineupComparison.isPartialSubmission ? (
            <CheckCircle className="w-5 h-5 text-[#23F8B5]" />
          ) : lineupComparison.status === FIELD_COMPARISON.MATCH ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-red-500" />
          )}
          <span className={`text-sm font-medium ${
            lineupComparison.isPartialSubmission
              ? 'text-[#23F8B5]'
              : lineupComparison.status === FIELD_COMPARISON.MATCH
                ? 'text-green-400'
                : 'text-red-300'
          }`}>
            {lineupComparison.isPartialSubmission
              ? 'Single team submission - lineup ready for review and approval'
              : lineupComparison.status === FIELD_COMPARISON.MATCH
                ? 'Lineup validation passed - all players are eligible'
                : 'Lineup validation issues detected - review required'
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default LineupValidationPanel;